// Mercapoli - Política de Privacidad
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Lock, Mail, MapPin, Bell, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

const PrivacyPolicy = () => {
  const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";
  const lastUpdated = "31 de Diciembre, 2024";

  const sections = [
    {
      icon: Database,
      title: "Información que Recopilamos",
      content: [
        "**Datos de registro**: nombre, correo electrónico, número de teléfono.",
        "**Datos de ubicación**: utilizamos tu ubicación SOLO cuando usas activamente la app para conectarte con comercios cercanos y realizar entregas.",
        "**Datos de pedidos**: historial de compras, direcciones de entrega, preferencias.",
        "**Datos de pago**: procesados de forma segura por proveedores externos (Stripe, MercadoPago). NO almacenamos datos de tarjetas."
      ]
    },
    {
      icon: Eye,
      title: "Cómo Usamos tu Información",
      content: [
        "Procesar y entregar tus pedidos.",
        "Conectarte con comercios y repartidores cercanos.",
        "Enviarte notificaciones sobre el estado de tus pedidos.",
        "Mejorar nuestros servicios y experiencia de usuario.",
        "Cumplir con obligaciones legales."
      ]
    },
    {
      icon: MapPin,
      title: "Uso de Ubicación",
      content: [
        "**Clientes**: Usamos tu ubicación para mostrarte comercios cercanos y calcular tarifas de entrega.",
        "**Repartidores**: Usamos tu ubicación SOLO cuando estás en línea y durante entregas activas para compartirla con el cliente.",
        "Puedes desactivar el acceso a la ubicación en cualquier momento desde la configuración de tu dispositivo.",
        "NO rastreamos tu ubicación cuando la app está cerrada."
      ]
    },
    {
      icon: Bell,
      title: "Notificaciones",
      content: [
        "Enviamos notificaciones para informarte sobre el estado de tus pedidos.",
        "Puedes desactivar las notificaciones en cualquier momento desde la configuración de tu dispositivo.",
        "Las notificaciones de proximidad te alertan cuando tu pedido está cerca."
      ]
    },
    {
      icon: Lock,
      title: "Seguridad de Datos",
      content: [
        "Utilizamos encriptación SSL/TLS para proteger tus datos en tránsito.",
        "Tus contraseñas se almacenan de forma segura usando hash criptográfico.",
        "Implementamos medidas de seguridad para prevenir accesos no autorizados.",
        "Realizamos auditorías de seguridad periódicas."
      ]
    },
    {
      icon: Trash2,
      title: "Tus Derechos",
      content: [
        "**Acceso**: Puedes solicitar una copia de tus datos personales.",
        "**Rectificación**: Puedes actualizar tus datos desde tu perfil.",
        "**Eliminación**: Puedes eliminar tu cuenta y datos desde la app (Perfil → Eliminar cuenta).",
        "**Portabilidad**: Puedes solicitar tus datos en formato exportable.",
        "Para ejercer estos derechos, contáctanos en: privacidad@mercapoli.com"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="Mercapoli" className="h-8 w-8" />
                <span className="font-bold text-lg">Mercapoli</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Política de Privacidad</h1>
          <p className="text-muted-foreground">Última actualización: {lastUpdated}</p>
        </div>

        {/* Introduction */}
        <div className="prose prose-sm dark:prose-invert max-w-none mb-8">
          <p className="text-lg">
            En Mercapoli, nos comprometemos a proteger tu privacidad. Esta política explica cómo 
            recopilamos, usamos y protegemos tu información personal cuando utilizas nuestra plataforma 
            de delivery de bienes físicos (comida, productos, flores, etc.).
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={index} className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <section.icon className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold">{section.title}</h2>
              </div>
              <ul className="space-y-3">
                {section.content.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-muted-foreground">
                    <span className="text-primary mt-1">•</span>
                    <span dangerouslySetInnerHTML={{ 
                      __html: item.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>') 
                    }} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact */}
        <div className="mt-8 p-6 bg-muted rounded-xl text-center">
          <Mail className="h-8 w-8 mx-auto mb-3 text-primary" />
          <h3 className="font-bold mb-2">¿Preguntas sobre privacidad?</h3>
          <p className="text-muted-foreground mb-4">
            Contáctanos en <a href="mailto:privacidad@mercapoli.com" className="text-primary hover:underline">privacidad@mercapoli.com</a>
          </p>
        </div>

        {/* Footer links */}
        <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
          <Link to="/terminos" className="text-primary hover:underline">Términos y Condiciones</Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/" className="text-primary hover:underline">Volver al inicio</Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
